import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, ShieldCheck, Target } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useTemplateDetail } from '@/api/hooks/useAttestations';
import { usePatchQuestions, usePatchSections, usePatchBypassConfig, usePatchQuestionBypassEligibility, usePatchTemplate, type QuestionUpdatePatch } from '@/api/hooks/useTemplateEdit';
import { PublishTemplateButton } from '@/components/templates/PublishTemplateButton';
import { SectionEditor } from '@/components/templates/SectionEditor';
import { ImportedLicenseBanner } from '@/features/templates/ImportedLicenseNotice';
import { useAuthStore } from '@/store/auth.store';

export function TemplateAuthoringPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { tenantId } = useAuthStore();

  useEffect(() => { document.title = 'Edit Template — VeriGRC'; }, []);

  const { data: template, isLoading, isError } = useTemplateDetail(id);
  const patchQuestions = usePatchQuestions(id ?? '');
  const patchSections = usePatchSections(id ?? '');
  const patchBypassConfig = usePatchBypassConfig(id ?? '');
  const patchQuestionBypassEligibility = usePatchQuestionBypassEligibility(id ?? '');
  const patchTemplate = usePatchTemplate(id ?? '');

  // #2 pass threshold — editable control seeded from the persisted template value; resync when it
  // changes (e.g. after a successful save refetch). Kept as a string for a controlled number input.
  const [thresholdInput, setThresholdInput] = useState('');
  useEffect(() => {
    if (template?.pass_threshold_pct != null) setThresholdInput(String(template.pass_threshold_pct));
  }, [template?.pass_threshold_pct]);

  const isMutating = patchQuestions.isPending || patchSections.isPending || patchBypassConfig.isPending || patchTemplate.isPending;

  async function handleAddQuestion(sectionId: string, text: string, responseType: string, maxScore: number, isRequired: boolean) {
    try {
      await patchQuestions.mutateAsync([
        { op: 'add', section_id: sectionId, question_text: text, response_type: responseType, max_score: maxScore, is_required: isRequired },
      ]);
    } catch {
      toast.error('Failed to add question.');
    }
  }

  async function handleDeleteQuestion(questionId: string) {
    try {
      await patchQuestions.mutateAsync([{ op: 'delete', id: questionId }]);
    } catch {
      toast.error('Failed to delete question.');
    }
  }

  async function handleUpdateQuestion(questionId: string, patch: QuestionUpdatePatch) {
    try {
      await patchQuestions.mutateAsync([{ op: 'update', id: questionId, ...patch }]);
    } catch {
      toast.error('Failed to update question.');
    }
  }

  async function handleAddSection(name: string, description?: string) {
    try {
      await patchSections.mutateAsync([
        { op: 'add', name, description: description ?? null },
      ]);
    } catch {
      toast.error('Failed to add section.');
    }
  }

  async function handleDeleteSection(sectionId: string) {
    try {
      await patchSections.mutateAsync([{ op: 'delete', id: sectionId }]);
    } catch {
      toast.error('Failed to delete section.');
    }
  }

  async function handleUpdateSection(sectionId: string, name: string, description: string | null) {
    try {
      await patchSections.mutateAsync([
        { op: 'update', id: sectionId, name, description },
      ]);
    } catch {
      toast.error('Failed to update section.');
    }
  }

  // #3 section weight — a weight-only partial update (safe post the section-repo undefined-guard).
  // Edits save even when the running total ≠ 100 (rebalancing needs invalid intermediate states);
  // the ≠100 gate is on PUBLISH, not on each save.
  async function handleUpdateSectionWeight(sectionId: string, weight: number) {
    try {
      await patchSections.mutateAsync([{ op: 'update', id: sectionId, weight_pct: weight }]);
    } catch {
      toast.error('Failed to update section weight.');
    }
  }

  // S4 — reorder via the revived reorder ops (display_order persists + survives publish).
  async function handleReorderSections(orderedIds: string[]) {
    try {
      await patchSections.mutateAsync([{ op: 'reorder', ordered_ids: orderedIds }]);
    } catch {
      toast.error('Failed to reorder sections.');
    }
  }
  async function handleReorderQuestions(sectionId: string, orderedIds: string[]) {
    try {
      await patchQuestions.mutateAsync([{ op: 'reorder', section_id: sectionId, ordered_ids: orderedIds }]);
    } catch {
      toast.error('Failed to reorder questions.');
    }
  }

  async function handleToggleAllowBypass(allow: boolean) {
    try {
      await patchBypassConfig.mutateAsync({ allow_bypass: allow });
      toast.success(allow ? 'Certification bypass enabled.' : 'Certification bypass disabled.');
    } catch {
      toast.error('Failed to update bypass setting.');
    }
  }

  async function handleUpdateBypassEligibility(questionId: string, certTypes: string[]) {
    try {
      await patchQuestionBypassEligibility.mutateAsync({ questionId, certTypes });
      toast.success(certTypes.length ? 'Bypass cert types saved.' : 'Bypass cert types cleared.');
    } catch {
      toast.error('Failed to update bypass eligibility.');
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6 max-w-3xl">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-96" />
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (isError || !template) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
          Back
        </Button>
        <p className="text-sm text-text-secondary">Template not found or could not be loaded.</p>
      </div>
    );
  }

  if (template.is_hub_template) {
    navigate(`/templates/${id}`, { replace: true });
    return null;
  }

  const totalQuestions = template.sections.reduce((s, sec) => s + sec.questions.length, 0);
  const lifecycleState = template.lifecycle_state ?? 'draft';
  const allowBypass = template.allow_bypass ?? false;

  // #3 section weights — the combined total (persisted values). Must equal exactly 100 to publish.
  const weightTotal = template.sections.reduce((s, sec) => s + (sec.weight_pct ?? 0), 0);
  const weightsBalanced = weightTotal === 100;

  // #2 pass threshold — integer 0–100 (matches the DB CHECK; no decimals). Save enabled only when
  // valid AND changed from the persisted value.
  const thresholdNum = Number(thresholdInput);
  const thresholdValid =
    thresholdInput.trim() !== '' && Number.isInteger(thresholdNum) && thresholdNum >= 0 && thresholdNum <= 100;
  const thresholdChanged = thresholdValid && thresholdNum !== template.pass_threshold_pct;

  async function handleSaveThreshold() {
    if (!thresholdValid) {
      toast.error('Pass threshold must be a whole number between 0 and 100.');
      return;
    }
    try {
      await patchTemplate.mutateAsync({ pass_threshold_pct: thresholdNum });
      toast.success('Pass threshold updated.');
    } catch {
      toast.error('Failed to update pass threshold.');
    }
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/templates/${id}`)} className="-ml-2">
          <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
          Back
        </Button>
        {isMutating && <Loader2 className="h-4 w-4 animate-spin text-text-muted" />}
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-semibold text-text-primary">{template.name}</h1>
            {lifecycleState === 'draft' && (
              <Badge variant="muted">Draft</Badge>
            )}
          </div>
          {template.description && (
            <p className="text-sm text-text-secondary max-w-xl">{template.description}</p>
          )}
        </div>
        <PublishTemplateButton
          templateId={template.id}
          tenantId={tenantId ?? ''}
          lifecycleState={lifecycleState}
          sectionCount={template.sections.length}
          questionCount={totalQuestions}
          weightsBalanced={weightsBalanced}
          weightTotal={weightTotal}
          onPublished={() => navigate(`/templates/${id}`)}
        />
      </div>

      {/* ITEM 3a(i) — imported-template licensing banner (hub_template_id != null). */}
      {template.hub_template_id && <ImportedLicenseBanner />}

      {/* #2 Pass threshold (editable; persists via PATCH /:id pass_threshold_pct) */}
      <div className="flex items-center justify-between rounded-lg border border-border-subtle bg-bg-surface px-4 py-3">
        <div className="flex items-center gap-2">
          <Target className="h-4 w-4 text-text-secondary" />
          <div>
            <p className="text-sm font-medium text-text-primary">Pass threshold</p>
            <p className="text-xs text-text-secondary">Minimum overall score a vendor must reach to pass this assessment.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min={0}
            max={100}
            step={1}
            value={thresholdInput}
            onChange={(e) => setThresholdInput(e.target.value)}
            aria-label="Pass threshold percent"
            aria-invalid={!thresholdValid}
            className="w-20 text-sm h-8"
          />
          <span className="text-sm text-text-secondary">%</span>
          <Button
            size="sm"
            onClick={handleSaveThreshold}
            disabled={patchTemplate.isPending || !thresholdChanged}
          >
            Save
          </Button>
        </div>
      </div>

      {/* Certification bypass toggle (risk_manager authoring setting) */}
      <div className="flex items-center justify-between rounded-lg border border-border-subtle bg-bg-surface px-4 py-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-text-secondary" />
          <div>
            <p className="text-sm font-medium text-text-primary">Allow certification bypass</p>
            <p className="text-xs text-text-secondary">Vendors may attest applicable certifications to bypass covered questions.</p>
          </div>
        </div>
        <button
          type="button"
          disabled={patchBypassConfig.isPending}
          onClick={() => handleToggleAllowBypass(!allowBypass)}
          className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none disabled:opacity-50 ${allowBypass ? 'bg-accent-emerald' : 'bg-bg-surface-2 border-border-subtle'}`}
          role="switch"
          aria-checked={allowBypass}
        >
          <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${allowBypass ? 'translate-x-4' : 'translate-x-0'}`} />
        </button>
      </div>

      {/* #3 Section-weight summary — live total + balanced indicator (weights sum must equal 100) */}
      {template.sections.length > 0 && (
        <div
          className={`flex items-center justify-between rounded-lg border px-4 py-2.5 text-sm ${
            weightsBalanced
              ? 'border-accent-emerald/40 bg-accent-emerald/10'
              : 'border-accent-amber/40 bg-accent-amber/10'
          }`}
        >
          <span className="text-text-secondary">Section weights</span>
          {weightsBalanced ? (
            <span className="font-medium text-accent-emerald">100% ✓ balanced</span>
          ) : (
            <span className="font-medium text-accent-amber">{weightTotal}% — must equal 100% before publishing</span>
          )}
        </div>
      )}

      <SectionEditor
        sections={template.sections.slice().sort((a, b) => a.display_order - b.display_order)}
        onAddSection={handleAddSection}
        onDeleteSection={handleDeleteSection}
        onUpdateSection={handleUpdateSection}
        onUpdateSectionWeight={handleUpdateSectionWeight}
        onReorderSections={handleReorderSections}
        onAddQuestion={handleAddQuestion}
        onDeleteQuestion={handleDeleteQuestion}
        onUpdateQuestion={handleUpdateQuestion}
        onReorderQuestions={handleReorderQuestions}
        onUpdateBypassEligibility={allowBypass ? handleUpdateBypassEligibility : undefined}
        allowBypass={allowBypass}
      />
    </div>
  );
}
